import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TimeService } from '@bayclubs/utils';
import * as moment from 'moment';

import { DatePickerModel } from '../date-picker.component';

@Component({
    selector: 'app-month-picker',
    templateUrl: './month-picker.component.html',
    styleUrls: ['./month-picker.component.scss', '../picker-form.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MonthPickerComponent {
    readonly isOpen = input(false);
    readonly min = input<moment.Moment | undefined>(undefined);
    readonly max = input<moment.Moment | undefined>(undefined);
    readonly model = input.required<DatePickerModel>();

    readonly months = computed<moment.Moment[]>(() => {
        const date = this.model().date();
        if (!date) return [];
        const result: moment.Moment[] = [];
        let start = date.clone().month(0);
        for (let i = 0; i < 12; i++) {
            result.push(start);
            start = this.timeService.addMonths(start, 1);
        }
        return result;
    });

    constructor(private timeService: TimeService) {}

    public isSelected(date: moment.Moment): boolean {
        return this.model().month() === date.month();
    }

    isMonthDisabled(date: moment.Moment): boolean {
        return this.isSelectedMinorMonthThanMinMarginal(date) || this.isSelectedGreaterMonthThanMaxMarginal(date);
    }

    isDayDisabled(date: moment.Moment): boolean {
        return this.isSelectedMinorDayThanMinMarginal(date) || this.isSelectedGreaterDayThanMaxMarginal(date);
    }

    isSelectedGreaterMonthThanMaxMarginal(date: moment.Moment): boolean {
        const y = this.model().year();
        const max = this.max();
        return y != null && max != null && y === max.year() && date.month() > max.month();
    }

    isSelectedMinorMonthThanMinMarginal(date: moment.Moment): boolean {
        const y = this.model().year();
        const min = this.min();
        return y != null && min != null && y === min.year() && min.month() > date.month();
    }

    isSelectedGreaterDayThanMaxMarginal(date: moment.Moment): boolean {
        const y = this.model().year();
        const max = this.max();
        return y != null && max != null && y === max.year() && date.month() === max.month() && date.date() > max.date();
    }

    isSelectedMinorDayThanMinMarginal(date: moment.Moment): boolean {
        const y = this.model().year();
        const min = this.min();
        return y != null && min != null && y === min.year() && min.month() === date.month() && min.date() > date.date();
    }

    selectMonth(date: moment.Moment): void {
        if (this.isMonthDisabled(date)) return;
        const m = this.model();
        const cloned = date.clone();
        m.date.set(cloned);
        m.month.set(cloned.month());
        this.clearCells();
    }

    clearCells(): void {
        const m = this.model();
        const day = m.day();
        const date = m.date();
        if (day != null && date && (date.daysInMonth() < day || this.isDayDisabled(date))) {
            m.day.set(null);
            m.date.set(this.timeService.startOfMonth(date));
        }
    }

    trackByMonth = (_: number, m: moment.Moment) => m.month();
}
